import { Close } from '@mui/icons-material';
import { FormControl, IconButton, Option, Select, SelectStaticProps } from '@mui/joy';
import { useRef } from 'react';
import { Controller } from 'react-hook-form';

import { FormLabel } from 'core/elements';
import { toFieldFocusRef } from 'core/utils/fieldFocus';
import { Color, Variant } from 'theme/enums';

import { IProps } from './Select';

const SelectWithControllerView = ({
  control,
  name,
  label,
  tooltip,
  required = false,
  options,
  error,
  defaultValue,
  setValue,
  getValue,
  getLabel,
  onChange,
  placeholder = '',
  size,
  disabled = false,
  clearable = false,
  multiple = false,
  wrapperStyle = {},
  ...rest
}: IProps) => {
  const action: SelectStaticProps['action'] = useRef(null);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { ref, ...field } }) => {
        return (
          <FormControl error={Boolean(error)} sx={wrapperStyle}>
            {label && (
              <FormLabel required={required} tooltip={tooltip}>
                {label}
              </FormLabel>
            )}
            <Select
              {...field}
              {...rest}
              multiple={multiple}
              action={action}
              // Focus the button: the root is a div and the hidden input cannot take focus.
              slotProps={{ button: { ref: toFieldFocusRef(ref) } }}
              defaultValue={defaultValue}
              placeholder={placeholder}
              onChange={(_, newValue) => {
                field.onChange(newValue);

                if (onChange) {
                  onChange(_, newValue);
                }
              }}
              size={size}
              disabled={disabled}
              {...(clearable &&
                (multiple ? Array.isArray(field.value) && field.value.length > 0 : field.value) && {
                  endDecorator: (
                    <IconButton
                      variant={Variant.PLAIN}
                      color={Color.NEUTRAL}
                      onMouseDown={(event) => {
                        // don't open the popup when clicking on this button
                        event.stopPropagation();
                      }}
                      onClick={() => {
                        setValue(name, multiple ? [] : null);
                        action.current?.focusVisible();
                      }}
                    >
                      <Close />
                    </IconButton>
                  ),
                  indicator: null,
                })}
            >
              {options?.map((option, index) => (
                <Option key={index} value={getValue(option)}>
                  {getLabel(option)}
                </Option>
              ))}
            </Select>
          </FormControl>
        );
      }}
    />
  );
};

export default SelectWithControllerView;
